import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    LayoutDashboard,
    LogOut,
    Download,
} from "lucide-react";
import { accountRequest } from "@/accountApi";
import { getSiteConfig } from "@/siteConfig";

const items = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
];

export default function DashboardShell({
    eyebrow,
    title,
    subtitle,
    actions,
    children,
    licence,
    site,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [session, setSession] = useState(null);
    const [checking, setChecking] = useState(true);
    const fallbackConfig = getSiteConfig();
    const version = site?.version || fallbackConfig.version;
    const downloadUrl = site?.downloadUrl || "";

    useEffect(() => {
        let cancelled = false;
        accountRequest("/api/account/session")
            .then((payload) => {
                if (!cancelled) setSession(payload.user);
            })
            .catch(() => {
                if (!cancelled) {
                    navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
                }
            })
            .finally(() => {
                if (!cancelled) setChecking(false);
            });
        return () => {
            cancelled = true;
        };
    }, [location.pathname, navigate]);

    const signOut = async () => {
        try {
            await accountRequest("/api/account/logout", { method: "POST", body: {} });
        } catch {
            // Clearing the local route is more important than surfacing a logout transport error.
        }
        toast.success("Signed out");
        navigate("/", { replace: true });
    };

    if (checking) {
        return (
            <div className="min-h-screen bg-bg text-ink flex items-center justify-center p-8">
                <div className="border border-ink p-6 bg-surface font-mono uppercase tracking-[0.2em] text-xs text-ink-mute">
                    Checking session...
                </div>
            </div>
        );
    }

    const userEmail = session?.email || "";
    const tier = licence?.planLabel || "No linked licence yet";
    const renewal = licence?.expiresAt || licence?.currentPeriodEnd || "Not available";

    return (
        <div data-testid="dashboard-shell" className="min-h-screen bg-bg text-ink">
            <header className="sticky top-0 z-40 bg-bg/95 backdrop-blur border-b border-border">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2">
                        <span className="font-display text-[20px] font-black tracking-tighter">ReachForge</span>
                        <span className="overline mt-1">/ Customer area</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        {downloadUrl && (
                            <a
                                data-testid="shell-cta-download"
                                href={downloadUrl}
                                className="hidden md:inline-flex btn h-9 px-3 text-xs border-ink bg-bg text-ink hover:bg-ink hover:text-bg transition-colors"
                            >
                                <Download size={12} /> v{version}
                            </a>
                        )}
                        <span className="hidden sm:inline text-sm text-ink-soft">{userEmail}</span>
                        <button data-testid="shell-signout" onClick={signOut} className="btn btn-secondary h-9 px-3 text-xs">
                            <LogOut size={12} /> Sign out
                        </button>
                    </div>
                </div>
                <nav aria-label="Dashboard sections" className="md:hidden border-t border-border overflow-x-auto" data-testid="shell-nav-mobile">
                    <div className="flex">
                        {items.map((it) => {
                            const Icon = it.icon;
                            return (
                                <NavLink
                                    key={it.to}
                                    to={it.to}
                                    end={it.end}
                                    data-testid={`shell-mobile-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `inline-flex items-center gap-2 px-4 py-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                                            isActive
                                                ? "border-forge text-ink"
                                                : "border-transparent text-ink-mute hover:text-ink"
                                        }`
                                    }
                                >
                                    <Icon size={14} strokeWidth={2.25} />
                                    {it.label}
                                </NavLink>
                            );
                        })}
                    </div>
                </nav>
            </header>

            <div className="mx-auto max-w-[1600px] grid md:grid-cols-[240px_1fr]">
                <aside data-testid="shell-sidebar" className="hidden md:flex md:flex-col border-r border-border min-h-[calc(100vh-4rem)] sticky top-16 self-start">
                    <nav aria-label="Dashboard sections" className="flex-1 py-6 pr-4 pl-6 lg:pl-10">
                        <div className="overline mb-4">Customer area</div>
                        <ul className="space-y-1">
                            {items.map((it) => {
                                const Icon = it.icon;
                                return (
                                    <li key={it.to}>
                                        <NavLink
                                            to={it.to}
                                            end={it.end}
                                            data-testid={`shell-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                            className={({ isActive }) =>
                                                `relative flex items-center gap-3 pl-4 pr-3 py-2.5 text-sm font-medium transition-colors ${
                                                    isActive
                                                        ? "bg-surface text-ink"
                                                        : "text-ink-soft hover:text-ink hover:bg-surface/60"
                                                }`
                                            }
                                        >
                                            {({ isActive }) => (
                                                <>
                                                    <span aria-hidden className={`absolute left-0 top-0 bottom-0 w-[3px] ${isActive ? "bg-forge" : "bg-transparent"}`} />
                                                    <Icon size={14} strokeWidth={2.25} />
                                                    {it.label}
                                                </>
                                            )}
                                        </NavLink>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="mt-10 border-t border-border pt-6">
                            <div className="overline mb-2">Licence</div>
                            <div className="font-display font-bold text-ink">{tier}</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute mt-1">
                                Valid until {renewal}
                            </div>
                            <Link to="/pricing" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-ink link-underline">
                                Pricing and plans
                            </Link>
                        </div>
                    </nav>
                </aside>

                <main className="px-6 md:px-10 lg:px-14 py-10 md:py-14">
                    {(eyebrow || title || actions) && (
                        <div className="grid lg:grid-cols-12 gap-6 items-end mb-10 md:mb-12">
                            <div className="lg:col-span-8">
                                {eyebrow && <span className="overline">{eyebrow}</span>}
                                {title && (
                                    <h1 data-testid="shell-page-title" className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mt-3 leading-[1.05] text-ink">
                                        {title}
                                    </h1>
                                )}
                                {subtitle && <p className="mt-3 text-ink-soft max-w-xl">{subtitle}</p>}
                            </div>
                            {actions && <div className="lg:col-span-4 flex flex-col gap-3">{actions}</div>}
                        </div>
                    )}
                    {children}

                    <p className="mt-14 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-mute">
                        Customer account area · Desktop lead data stays local unless you sync it explicitly.
                    </p>
                </main>
            </div>
        </div>
    );
}
